import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useInView } from 'react-intersection-observer';
import Heading from 'components/shared/heading';
import PropTypes from 'prop-types';
import PlusCircledIcon from '../../icons/PlusCircled';
import styles from './styles.module.scss';

const WistiaPlayerLazy = lazy(() => import('../../shared/wistia-player'));

const ProductViewerGallery = ({ theme = '', title = null, items = [] }) => {
  const [expandedIndex, setExpandedIndex] = useState(0); // Start with "Evidence" expanded
  const containerRef = useRef(null);
  const wistiaPlayerRefs = useRef([]);
  const [sectionRef, inView] = useInView({ threshold: 0.33 });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const toggleItem = useCallback((index) => {
    setExpandedIndex((prev) => {
      wistiaPlayerRefs.current[prev]?.pause();
      wistiaPlayerRefs.current[index]?.play();
      return prev === index ? null : index;
    });
  }, []);

  // Inject images once when sections arrive
  useEffect(() => {
    let timeoutId = null;

    if (items && items.length > 0 && containerRef?.current) {
      timeoutId = setTimeout(() => {
        items.forEach((item) => {
          if (!item?.image?.id) return;
          const parent = containerRef?.current?.parentElement;
          const source = parent?.querySelector(`#pvg-img-${item.image.id}`);
          const target = parent?.querySelector(`#pvg-img-box-${item.image.id}`);

          if (source && target) {
            target.innerHTML = '';
            target.appendChild(source.cloneNode(true));
          }
        });
      }, 100);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [items]);

  const placeStyleToWistia = useCallback((wistiaElementParam, index) => {
    const wistiaElement = wistiaElementParam || wistiaPlayerRefs.current[index];
    // This runs after the Wistia player component has been rendered
    // setTimeout(() => {
    // Find the Shadow DOM
    const { shadowRoot } = wistiaElement;
    if (shadowRoot) {
      // Create a style element to inject our CSS
      const styleEl = document.createElement('style');
      styleEl.textContent = `
              /* This CSS is now safely inside the Shadow DOM */
              video {
                width: 100% !important;
                height: 100% !important;
                object-fit: cover !important;
                position: absolute !important;
                top: 0;
                left: 0;
              }
              div {
                position: initial !important;
              }
              .click-for-sound-btn {
                display: none !important;
              }
          `;
      shadowRoot.appendChild(styleEl);
    }
  }, []);

  const handleApiReady = useCallback(
    (index) => (event) => {
      const player = event?.target;
      if (!player) return;
      wistiaPlayerRefs.current[index] = player;
      placeStyleToWistia(player, index);
    },
    [placeStyleToWistia]
  );

  return (
    <section
      className={cn(styles.root, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
        [styles.inView]: inView,
      })}
      ref={(node) => {
        containerRef.current = node;
        sectionRef(node);
      }}
    >
      {title && (
        <Heading className={styles.title} tag="h2" size="xl">
          {title}
        </Heading>
      )}
      <div className="container">
        <div className={styles.mediaContainer}>
          {items.map((item, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <div
                key={index}
                className={cn(styles.mediaItem, isExpanded && styles.expanded)}
                id={item.videoId ? undefined : `pvg-img-box-${item.image.id}`}
              >
                {isClient && item.videoId && (
                  <Suspense fallback={null}>
                    <WistiaPlayerLazy
                      mediaId={item.videoId}
                      ref={wistiaPlayerRefs.current[index]}
                      onApiReady={handleApiReady(index)}
                      autoPlay={false}
                      playlistLoop
                      muted
                      className={styles.wistiaPlayer}
                      poster={false}
                      swatch={false}
                      controls={false}
                      resumable
                    />
                  </Suspense>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.accordion}>
              {items.map((item, index) => {
                const isExpanded = expandedIndex === index;
                return (
                  <div
                    key={index}
                    className={cn(styles.accordionItem, isExpanded && styles.expanded)}
                  >
                    <button
                      className={styles.accordionButton}
                      onClick={() => toggleItem(index)}
                      aria-expanded={isExpanded}
                      type="button"
                    >
                      <PlusCircledIcon />
                      <span className={styles.itemTitle}>{item.title}</span>
                    </button>
                    {item.description && (
                      <div className={styles.accordionContent}>
                        <div className={styles.accordionContentItem}>
                          <strong>{item.title}.</strong>&nbsp;
                          <div dangerouslySetInnerHTML={{ __html: item.description }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

ProductViewerGallery.propTypes = {
  theme: PropTypes.oneOf(['', 'light', 'dark']),
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      buttonLabel: PropTypes.string,
      buttonLink: PropTypes.string,
      videoId: PropTypes.string,
      image: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    })
  ),
};

export default ProductViewerGallery;
